import { env } from '@/env.mjs'
import { getPathname } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'
import {
	ROOT_APPS_PATH,
	ROOT_BLOG_PAGE_PATH,
	ROOT_CASE_STUDIES_PATH,
	ROOT_PAGE_PATH,
} from '@/lib/constants'
import { getOriginUrl } from '@/lib/general-helpers'
import type { AppLocale } from '@/types/general'

type LocalizedHref = Parameters<typeof getPathname>[0]['href']

function getLocalizedPath(href: LocalizedHref, locale?: AppLocale) {
	return getPathname({ locale: locale ?? routing.defaultLocale, href })
}

function getLocalePrefix(locale?: AppLocale) {
	if (!locale || locale === routing.defaultLocale) {
		return ''
	}

	return `/${locale}`
}

/**
 * Use getLocalizedPath for routes in routing config.
 * Use buildLocalePrefixedPath for raw path strings not covered by routing.
 */
function buildLocalePrefixedPath(pathname: string, locale?: AppLocale) {
	const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`
	return `${getLocalePrefix(locale)}${normalizedPath}`
}

export const getFullUrl = (relativeLink: string | null | undefined) => {
	if (relativeLink == null) {
		return undefined
	}

	if (relativeLink === '/') {
		return env.APP_PUBLIC_URL
	}

	if (relativeLink[0] === '/') {
		return `${env.APP_PUBLIC_URL}${relativeLink}`
	}

	return `${env.APP_PUBLIC_URL}/${relativeLink}`
}

export const getBlogLink = (locale?: AppLocale) => {
	return getLocalizedPath(ROOT_BLOG_PAGE_PATH, locale)
}

export const getArticleLink = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}

	return getLocalizedPath(
		{
			pathname: '/blog/[slug]',
			params: { slug },
		},
		locale,
	)
}

export const getArticleUrl = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}

	return getFullUrl(getArticleLink(slug, locale))
}

export const getBlogCategoryLink = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}

	return getLocalizedPath(
		{
			pathname: '/blog/category/[slug]',
			params: { slug },
		},
		locale,
	)
}

export const getBlogCategoryUrl = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}

	return getFullUrl(getBlogCategoryLink(slug, locale))
}

export const getAuthorLink = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}

	return getLocalizedPath(
		{
			pathname: '/blog/author/[slug]',
			params: { slug },
		},
		locale,
	)
}

export const getAuthorUrl = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}
	return getFullUrl(getAuthorLink(slug, locale))
}

export const getPageLink = (fullPath?: string | null, locale?: AppLocale) => {
	if (fullPath == null || fullPath === '/' || fullPath === ROOT_PAGE_PATH) {
		return ''
	}

	const path = fullPath.replace('index/', '')
	return buildLocalePrefixedPath(path, locale)
}

export const getPageUrl = (fullPath?: string | null, locale?: AppLocale) => {
	if (fullPath == null) {
		return ''
	}

	return getFullUrl(getPageLink(fullPath, locale))
}

export const getCaseStudiesLink = (slug?: string | null, locale?: AppLocale) => {
	if (slug == null) {
		return ''
	}
	return getLocalizedPath(
		{
			pathname: '/case-studies/[slug]',
			params: { slug },
		},
		locale,
	)
}

export const getCaseStudiesRootPageLink = (locale?: AppLocale) => {
	return getLocalizedPath(ROOT_CASE_STUDIES_PATH, locale)
}

export const getAppRootLink = (locale?: AppLocale) => {
	return getLocalizedPath(ROOT_APPS_PATH, locale)
}

export const getAppLink = (slug?: string | null, category?: string | null, locale?: AppLocale) => {
	if (slug == null || category == null) {
		return ''
	}
	return getLocalizedPath(
		{
			pathname: '/product/apps/[category]/[slug]',
			params: { category, slug },
		},
		locale,
	)
}

export const getAppCategoryLink = (category?: string | null, locale?: AppLocale) => {
	if (category == null) {
		return ''
	}
	return getLocalizedPath(
		{
			pathname: '/product/apps/[category]',
			params: { category },
		},
		locale,
	)
}

/**
 * Checks if a link is an internal app link (same hostname) or external
 */
export function isAppLink(link: string): boolean {
	try {
		const baseUrl = getOriginUrl()
		const url = new URL(link, baseUrl)
		return url.hostname === new URL(baseUrl).hostname
	} catch (_e) {
		return false
	}
}

/**
 * Checks if a value matches the pathname, accounting for trailing slashes
 */
export const isValueEqualToPathname = (value: string, pathname: string) =>
	value === pathname || `${value}/` === pathname
